/**
 * Human-readable copy for the canonical, machine-readable plugin-update failure
 * codes emitted by the updater (dictionary `PluginUpdateFailureReasons`, owned
 * by the engine / F3). Each message tells the user what happened and the next
 * action (Retry, check something, or contact support).
 *
 * Unknown / empty / unrecognised codes fall back to the raw `errorMessage` when
 * one is present, and otherwise to a generic retry prompt — so a new engine
 * code never renders a blank banner.
 */
const FAILURE_MESSAGES: Record<string, string> = {
  network_timeout:
    'Cannot reach the update server. Check your network connection and press Retry.',
  proxy_auth:
    'Could not authenticate with your network proxy. Check your proxy settings and press Retry.',
  sha256_mismatch:
    'The downloaded update is corrupted (checksum mismatch). Press Retry to download again.',
  size_mismatch:
    'The downloaded update is incomplete (size mismatch). Press Retry to download again.',
  zip_extract_failed:
    'The downloaded update could not be unpacked. Press Retry to download again.',
  payload_validation_failed:
    'The update package is missing required files. Press Retry; if the problem persists, contact support.',
  xml_flip_failed:
    'Could not activate the staged update. Press Retry; if the problem persists, contact support.',
  disk_io:
    'Could not write the update to the plugin folder. Check free disk space and folder permissions.',
  locked_by_other_session:
    'Another Revit session is installing the update. Close other Revit windows and press Retry.',
  untrusted_signature:
    'The update failed signature verification. Contact your administrator.',
};

const GENERIC_FAILURE = 'Update failed. Press Retry.';

export function humanizeFailureReason(code: string, raw: string): string {
  const mapped = FAILURE_MESSAGES[code];
  if (mapped) {
    return mapped;
  }

  const trimmedRaw = raw.trim();
  return trimmedRaw.length > 0 ? raw : GENERIC_FAILURE;
}
